"use strict";
/**
 * Cross-Platform Environment Configuration
 *
 * This module provides a unified way to access environment variables
 * across web, mobile (React Native), and Firebase Functions platforms.
 */
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.logEnvironmentInfo = exports.validateRequiredEnvVars = exports.platform = exports.features = exports.app = exports.tmdb = exports.firebase = exports.isProduction = exports.isDevelopment = exports.environment = exports.appConfig = exports.createAppConfig = void 0;
// Platform detection
const isWeb = typeof window !== 'undefined';
const isReactNative = typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
const isNode = typeof process !== 'undefined' && ((_a = process.versions) === null || _a === void 0 ? void 0 : _a.node);
/**
 * Get environment variable with platform-specific handling
 */
function getEnvVar(key, defaultValue) {
    var _a, _b;
    if (isNode) {
        // Node.js environment (Firebase Functions)
        return process.env[key] || defaultValue;
    }
    if (isWeb) {
        // Web environment (React)
        // In React, only variables prefixed with REACT_APP_ are available
        const reactKey = key.startsWith('REACT_APP_') ? key : `REACT_APP_${key}`;
        return process.env[reactKey] || defaultValue;
    }
    if (isReactNative) {
        // React Native environment
        // Use expo-constants or react-native-config for environment variables
        try {
            // Try expo-constants first
            const expoConstants = 'expo-constants';
            const Constants = require(expoConstants);
            return ((_b = (_a = Constants.expoConfig) === null || _a === void 0 ? void 0 : _a.extra) === null || _b === void 0 ? void 0 : _b[key]) || defaultValue;
        }
        catch (_c) {
            try {
                // Fallback to react-native-config
                const reactNativeConfig = 'react-native-config';
                const Config = require(reactNativeConfig);
                return Config[key] || defaultValue;
            }
            catch (_d) {
                console.warn(`Environment variable ${key} not available in React Native`);
                return defaultValue;
            }
        }
    }
    return defaultValue;
}
/**
 * Get environment type
 */
function getEnvironment() {
    const env = getEnvVar('NODE_ENV') || getEnvVar('ENVIRONMENT') || 'development';
    switch (env.toLowerCase()) {
        case 'production':
        case 'prod':
            return 'production';
        case 'staging':
        case 'stage':
            return 'staging';
        default:
            return 'development';
    }
}
/**
 * Get Firebase project configuration
 */
function getFirebaseConfig() {
    const environment = getEnvironment();
    // Default project IDs based on environment
    const defaultProjectIds = {
        development: 'watchlists-dev',
        staging: 'watchlists-staging',
        production: 'watchlists-prod'
    };
    const projectId = getEnvVar('FIREBASE_PROJECT_ID') ||
        getEnvVar('REACT_APP_FIREBASE_PROJECT_ID') ||
        defaultProjectIds[environment];
    const region = getEnvVar('FIREBASE_REGION') ||
        getEnvVar('REACT_APP_FIREBASE_REGION') ||
        'us-central1';
    const functionsUrl = getEnvVar('FIREBASE_FUNCTIONS_URL') ||
        getEnvVar('REACT_APP_FIREBASE_FUNCTIONS_URL');
    return {
        projectId,
        region,
        functionsUrl
    };
}
/**
 * Get TMDB configuration
 */
function getTmdbConfig() {
    // Always use Firebase Functions in production for security
    const environment = getEnvironment();
    const useFirebaseFunctions = environment === 'production' ||
        getEnvVar('USE_FIREBASE_FUNCTIONS') === 'true' ||
        getEnvVar('REACT_APP_USE_FIREBASE_FUNCTIONS') === 'true';
    const apiKey = getEnvVar('TMDB_API_KEY') ||
        getEnvVar('REACT_APP_TMDB_API_KEY');
    return {
        useFirebaseFunctions,
        apiKey: useFirebaseFunctions ? undefined : apiKey // Don't expose API key if using Functions
    };
}
/**
 * Get app configuration
 */
function getAppConfig() {
    return {
        name: getEnvVar('APP_NAME') || getEnvVar('REACT_APP_NAME') || 'Watchlists',
        version: getEnvVar('APP_VERSION') || getEnvVar('REACT_APP_VERSION') || '1.0.0',
        bundleId: getEnvVar('APP_BUNDLE_ID') || getEnvVar('REACT_APP_BUNDLE_ID') || 'com.watchlists.app'
    };
}
/**
 * Get feature flags
 */
function getFeatureFlags() {
    const environment = getEnvironment();
    // Web/Capacitor: set REACT_APP_ENABLE_ANALYTICS=true so GA4 loads and events/DebugView work in dev.
    // Production enables analytics by default (no env needed).
    return {
        enableAnalytics: environment === 'production' || getEnvVar('ENABLE_ANALYTICS') === 'true',
        enableCrashlytics: environment === 'production' || getEnvVar('ENABLE_CRASHLYTICS') === 'true',
        enablePerformanceMonitoring: environment === 'production' || getEnvVar('ENABLE_PERFORMANCE') === 'true',
        enableDebugMode: environment === 'development' || getEnvVar('DEBUG_MODE') === 'true'
    };
}
/**
 * Create complete app configuration
 */
function createAppConfig() {
    const environment = getEnvironment();
    return {
        environment,
        isDevelopment: environment === 'development',
        isProduction: environment === 'production',
        firebase: getFirebaseConfig(),
        tmdb: getTmdbConfig(),
        app: getAppConfig(),
        features: getFeatureFlags(),
        platform: {
            isWeb,
            isReactNative,
            isNode: !!isNode
        }
    };
}
exports.createAppConfig = createAppConfig;
// Export singleton configuration
exports.appConfig = createAppConfig();
// Convenience exports
exports.environment = exports.appConfig.environment, exports.isDevelopment = exports.appConfig.isDevelopment, exports.isProduction = exports.appConfig.isProduction;
exports.firebase = exports.appConfig.firebase, exports.tmdb = exports.appConfig.tmdb, exports.app = exports.appConfig.app, exports.features = exports.appConfig.features, exports.platform = exports.appConfig.platform;
// Environment validation helpers
function validateRequiredEnvVars() {
    const required = [
        'FIREBASE_PROJECT_ID'
    ];
    const missing = required.filter(key => !getEnvVar(key));
    if (missing.length > 0) {
        console.warn('Missing required environment variables:', missing);
        if (exports.isProduction) {
            throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
        }
    }
}
exports.validateRequiredEnvVars = validateRequiredEnvVars;
// Debug helper
function logEnvironmentInfo() {
    if (exports.isDevelopment || exports.features.enableDebugMode) {
        console.log('🔧 Environment Configuration:', {
            environment: exports.environment,
            platform: exports.platform,
            firebase: {
                projectId: exports.firebase.projectId,
                region: exports.firebase.region
            },
            tmdb: {
                useFirebaseFunctions: exports.tmdb.useFirebaseFunctions,
                hasApiKey: !!exports.tmdb.apiKey
            },
            features: exports.features
        });
    }
}
exports.logEnvironmentInfo = logEnvironmentInfo;
// Initialize validation on import
if (typeof window !== 'undefined' || typeof global !== 'undefined') {
    try {
        validateRequiredEnvVars();
        logEnvironmentInfo();
    }
    catch (error) {
        console.error('Environment configuration error:', error);
    }
}
